// IHOG proximity rings layer family.
//
// Two rings per active incident: outer contact (amber dashed) and
// inner exclusion (red solid). Visually distinct from TFR fills so
// they can coexist on the map without clashing — IHOG rings are
// STROKED ONLY; the TFR layer carries the red filled crosshatch.
// Data lives in the in-memory incidents store; React effect in MapView
// re-feeds the source whenever the list changes.
//
// NOTE: id suffix '-mount' to avoid colliding with IhogRingsController's
// existing registerStyleRebuild entry (id 'ihogRings'). The controller
// owns apply behavior — visibility AND data re-push after a basemap
// rebuild, gated on store subscriptions. If this module registered
// under 'ihogRings', it would silently replace the controller's restore
// in the registry Map.
//
// With a separate id, both restores run on basemap rebuild: this module
// re-mounts the sources/layers; the controller's 'ihogRings' entry then
// re-runs visibility + data re-push. Ordering between two registry entries
// isn't guaranteed, but the controller's apply() is guarded by
// map.getLayer('ihog-rings-fill') checks, so a no-op-then-mount-then-apply
// sequence is safe.
//
// See src/map/layers/terrain.ts for the canonical version of this pattern.
// Extracted from addOverlays.ts as HI-1 batch 7 round 4 (2026-05-24).
// Pure mechanical move — source spec, paint expressions, layer ids, minzoom,
// and layer order all byte-identical to the pre-extraction form.

import type { Map as MlMap } from 'maplibre-gl';
import { EMPTY_FC } from '../MapView';
import { registerLayerFamily } from '../layerVisibilityRegistry';

/**
 * Mount the IHOG proximity rings source and layers.
 * Idempotent on the source.
 */
export function addIhogRingsLayers(map: MlMap): void {
  if (!map.getSource('ihog-rings')) {
    map.addSource('ihog-rings', { type: 'geojson', data: EMPTY_FC });
    // Subtle fill on the exclusion ring only so the inner zone reads
    // as 'inside' at a glance. Contact ring stays unfilled to keep
    // the chart legible underneath.
    map.addLayer({
      id: 'ihog-rings-fill',
      type: 'fill',
      source: 'ihog-rings',
      filter: ['==', ['get', 'kind'], 'exclusion'],
      layout: { visibility: 'none' },
      paint: {
        'fill-color': '#dc2626',
        'fill-opacity': 0.10,
      },
    });
    // Stroked outlines. Color + dash pattern is driven by `kind`
    // so a single line layer handles both rings.
    map.addLayer({
      id: 'ihog-rings-line',
      type: 'line',
      source: 'ihog-rings',
      layout: { visibility: 'none', 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': [
          'case',
          ['==', ['get', 'kind'], 'exclusion'], '#dc2626',   // red
          /* contact */                          '#f59e0b',   // amber
        ],
        'line-width': [
          'interpolate', ['linear'], ['zoom'],
          4, 1.6,
          8, 2.4,
          12, 3.6,
        ],
        'line-dasharray': [
          'case',
          ['==', ['get', 'kind'], 'exclusion'], ['literal', [1]],     // solid
          /* contact */                          ['literal', [4, 2]],  // dashed
        ],
        'line-opacity': 0.95,
      },
    });
    // Labels show "<incident> · <radius> NM <kind>" so the pilot can
    // identify which fire's ring they're looking at. Placed along the
    // ring (symbol-placement: line) so the text follows the curve and
    // doesn't cover up the center of the incident.
    map.addLayer({
      id: 'ihog-rings-label',
      type: 'symbol',
      source: 'ihog-rings',
      layout: {
        visibility: 'none',
        'text-field': [
          'format',
          ['get', 'incidentName'], { 'font-scale': 1.0 },
          ' · ', {},
          ['concat', ['to-string', ['get', 'radiusNm']], ' NM'], { 'font-scale': 0.9 },
        ],
        'symbol-placement': 'line',
        'symbol-spacing': 600,
        'text-size': [
          'interpolate', ['linear'], ['zoom'],
          6, 10, 9, 12, 12, 14,
        ],
        'text-allow-overlap': false,
        'text-padding': 4,
      },
      paint: {
        'text-color': [
          'case',
          ['==', ['get', 'kind'], 'exclusion'], '#fca5a5',
          /* contact */                          '#fcd34d',
        ],
        'text-halo-color': '#0b0d0f',
        'text-halo-width': 1.4,
      },
      minzoom: 5,
    });
  }
}

// NOTE: id suffix '-mount' to avoid colliding with IhogRingsController's
// existing registerStyleRebuild entry (id 'ihogRings'). See comment at
// top of file and src/map/layers/terrain.ts for full explanation.
registerLayerFamily({
  id: 'ihogRings-mount',
  label: 'IHOG proximity rings mount',
  sourceId: 'ihog-rings',
  layerIds: ['ihog-rings-fill', 'ihog-rings-line', 'ihog-rings-label'],
  mount: (map) => addIhogRingsLayers(map),
  // NO applyState — IhogRingsController owns apply behavior.
});
